import { call, put, select, takeLatest } from "redux-saga/effects";
import {
  deleteConfiguration as deleteConfigurationApi,
  saveConfiguration as saveConfigurationApi,
} from "../../../services/coilConfiguratorApi";
import { buildConfigurationModalErrors } from "../../../web-app";
import { buildSavedConfigurationFromModal, recalculateSavedConfiguration } from "../../../components/CoilConfigurator/domain/configurationModal";
import type { RootState } from "../../appState";
import {
  closeConfigModal,
  openConfigurationDetail,
  showToast,
  upsertConfiguration,
} from "../actions";
import {
  SAVE_APPLICATION_STATE_SUCCESS,
  SUBMIT_CONFIG_MODAL,
  SUBMIT_CONFIG_MODAL_FAILURE,
  DELETE_CONFIGURATION,
  RECALCULATE_CONFIGURATION,
} from "../types";

function* submitConfigModalSaga(
  action: ReturnType<typeof import("../actions").submitConfigModal>,
): Generator {
  const errors = buildConfigurationModalErrors(action.values);

  if (Object.keys(errors).length > 0) {
    yield put({ type: SUBMIT_CONFIG_MODAL_FAILURE, fieldErrors: errors });
    return;
  }

  const state = (yield select((s: RootState) => s)) as RootState;
  const catalogue = state.coilConfigurator.catalogue.active;
  const assumptions = state.coilConfigurator.assumptions.active;

  if (!assumptions) {
    yield put({
      type: SUBMIT_CONFIG_MODAL_FAILURE,
      formError: "Recommendation assumptions are not loaded yet.",
    });
    return;
  }

  const { mode, editId } = state.coilConfigurator.ui.configModal;
  const existing =
    mode === "edit" && editId
      ? state.coilConfigurator.configurations.items.find((item) => item.id === editId)
      : undefined;

  try {
    const config = buildSavedConfigurationFromModal(
      action.values,
      mode,
      editId,
      catalogue,
      assumptions,
      existing?.createdAt,
    );

    yield call(saveConfigurationApi, config);
    yield put(upsertConfiguration(config));
    yield put(closeConfigModal());
    yield put(openConfigurationDetail(config.id));

    const updatedAt = new Date().toISOString();
    yield put({ type: SAVE_APPLICATION_STATE_SUCCESS, updatedAt });

    yield put(
      showToast(
        mode === "create"
          ? "Configuration created and saved"
          : "Configuration updated and saved",
      ),
    );
  } catch (error) {
    const message = error instanceof Error ? error.message : "Failed to save configuration";
    yield put({ type: SUBMIT_CONFIG_MODAL_FAILURE, formError: message });
    yield put(showToast(message, "error"));
  }
}

function* deleteConfigurationSaga(
  action: { type: typeof DELETE_CONFIGURATION; id: string },
): Generator {
  try {
    yield call(deleteConfigurationApi, action.id);
    const updatedAt = new Date().toISOString();
    yield put({ type: SAVE_APPLICATION_STATE_SUCCESS, updatedAt });
    yield put(showToast("Configuration deleted."));
  } catch (error) {
    const message = error instanceof Error ? error.message : "Failed to delete configuration";
    yield put(showToast(message, "error"));
  }
}

function* recalculateConfigurationSaga(
  action: { type: typeof RECALCULATE_CONFIGURATION; id: string },
): Generator {
  const state = (yield select((s: RootState) => s)) as RootState;
  const config = state.coilConfigurator.configurations.items.find((item) => item.id === action.id);

  if (!config) {
    yield put(showToast("Configuration not found.", "error"));
    return;
  }

  const catalogue = state.coilConfigurator.catalogue.active;
  const assumptions = state.coilConfigurator.assumptions.active;

  if (!assumptions) {
    yield put(showToast("Recommendation assumptions are not loaded yet.", "error"));
    return;
  }

  try {
    const updatedConfig = recalculateSavedConfiguration(config, catalogue, assumptions);
    yield call(saveConfigurationApi, updatedConfig);
    yield put(upsertConfiguration(updatedConfig));

    const updatedAt = new Date().toISOString();
    yield put({ type: SAVE_APPLICATION_STATE_SUCCESS, updatedAt });
    yield put(showToast("Configuration recalculated and saved."));
  } catch (error) {
    const message = error instanceof Error ? error.message : "Recalculation failed";
    yield put(showToast(message, "error"));
  }
}

function* configurationsWatcher(): Generator {
  yield takeLatest(SUBMIT_CONFIG_MODAL, submitConfigModalSaga);
  yield takeLatest(DELETE_CONFIGURATION, deleteConfigurationSaga);
  yield takeLatest(RECALCULATE_CONFIGURATION, recalculateConfigurationSaga);
}

export default configurationsWatcher;
